
import React, { useState } from 'react';
import { useJobList } from '@/hooks/useJobList';
import { LoadingState } from "@/components/error-handling/LoadingState";
import { ErrorState } from "@/components/error-handling/ErrorState";
import { JobFilters } from './JobFilters';
import { JobTableView } from './JobTableView';
import { JobGridView } from './JobGridView';
import { Job } from './types';

const JobList = () => {
  const { data: jobsData = [], isLoading, error } = useJobList();
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [filters, setFilters] = useState({ status: 'all', searchTerm: '' });

  // Transform the data to ensure proper typing and normalize status values
  const jobs: Job[] = jobsData.map(job => ({
    ...job,
    // Normalize status to match the Job type definition
    status: (['open', 'closed', 'on-hold', 'filled', 'pending', 'archived'].includes(job.status)) 
      ? job.status as Job['status']
      : 'pending' as Job['status']
  }));

  const handleFilterChange = (type: string, value: string) => {
    setFilters(prev => ({ ...prev, [type]: value }));
  };

  const filteredJobs = jobs.filter(job => {
    const statusMatch = filters.status === 'all' || job.status === filters.status;
    const termMatch = !filters.searchTerm || 
                     job.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                     (job.company && job.company.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
                     (job.client_name && job.client_name.toLowerCase().includes(filters.searchTerm.toLowerCase()));
    return statusMatch && termMatch;
  });

  if (isLoading) return <LoadingState message="Loading jobs..." />;
  if (error && error instanceof Error) return <ErrorState title="Error loading jobs" description={error.message} />;
  if (error) return <ErrorState title="Error loading jobs" description="An unknown error occurred" />;

  return (
    <div className="space-y-4">
      <JobFilters 
        filters={filters} 
        onFilterChange={handleFilterChange} 
        onViewModeChange={setViewMode} 
        currentViewMode={viewMode} 
      />

      {viewMode === 'table' ? (
        <JobTableView jobs={filteredJobs} />
      ) : (
        <JobGridView jobs={filteredJobs} />
      )}

       {filteredJobs.length === 0 && !isLoading && (
        <div className="text-center py-10 text-muted-foreground">
          No jobs match your current filters.
        </div>
      )}
    </div>
  );
};

export default JobList;
